import { create } from 'zustand';
import { groupsApi, chatApi, sessionsApi, challengesApi, leaderboardApi } from '../lib/apiClient';

export const useGroupStore = create((set, get) => ({
  groupId: null,
  groupName: '',
  pinnedMessage: null,
  members: [],
  challenges: [],
  chatMessages: [],
  leaderboard: [],
  globalLeaderboard: [],
  recentActivity: [],
  pendingRequests: [],
  weeklyMinimumHours: 20,
  weeklyContribution: 0,
  isLoading: false,

  // Fetch all group-specific data in parallel
  fetchGroupData: async (groupId) => {
    if (!groupId) return;
    set({ isLoading: true });
    try {
      const [groupRes, challengesRes, leaderboardRes, messagesRes, globalRes] = await Promise.all([
        groupsApi.getGroup(groupId).catch(() => ({ members: [] })),
        challengesApi.list(groupId).catch(() => []),
        leaderboardApi.getGroup(groupId).catch(() => []),
        chatApi.getMessages(groupId).catch(() => []),
        leaderboardApi.getGlobal(1).catch(() => [])
      ]);
      const membersRes = groupRes.members ?? [];

      // Map D1 formats to UI formats
      const mappedMembers = membersRes.map(m => ({
        id: m.id,
        name: m.display_name || m.name,
        avatar: m.avatar_index,
        role: m.role,
        weeklyHours: (m.weekly_seconds || 0) / 3600,
        streak: m.streak_days,
        joinDate: new Date((m.created_at || m.join_date || 0) * 1000).toISOString().split('T')[0],
        weeklyChange: (m.weekly_change || 0) / 3600,
        lastWeekHours: (m.last_week_seconds || 0) / 3600,
        isOnline: !!m.is_online,
        leavesUsed: m.leaves_used || 0,
        leavesAllowed: m.monthly_leaves || 5,
        paid: !!m.paid
      }));

      const mappedChallenges = challengesRes.map(c => ({
        id: c.id,
        title: c.title,
        description: c.description,
        topic: c.topic_tag,
        deadline: new Date(c.deadline_at * 1000).toISOString(),
        minHours: c.min_hours,
        xpReward: c.xp_reward,
        completedBy: c.completed_by || []
      }));

      const mappedMessages = messagesRes.map(m => ({
        id: m.id,
        userId: m.user_id,
        userName: m.display_name,
        userAvatar: m.avatar_index,
        tag: m.topic_tag === 'study_help' ? 'Study Help' : m.topic_tag === 'resource' ? 'Resource Share' : m.topic_tag === 'challenge' ? 'Challenge' : 'Off-topic',
        text: m.content,
        timestamp: new Date(m.created_at * 1000).toISOString()
      })).reverse();

      const mappedGlobal = globalRes.map((g, idx) => ({
        rank: g.rank ?? idx + 1,
        groupName: g.groupName ?? g.name,
        memberCount: g.memberCount ?? g.member_count ?? 0,
        avgHours: g.avgHours ?? ((g.avg_sec || 0) / 3600),
        topMembers: g.topMembers ?? g.top_members ?? [0, 1, 2]
      }));

      // Use the pinned_message from the group object
      const pinMsg = groupRes.group?.pinned_message ?? null;

      set({
        groupId,
        groupName: groupRes.group?.name || membersRes[0]?.group_name || 'My Group',
        members: mappedMembers,
        leaderboard: mappedMembers.sort((a, b) => b.weeklyHours - a.weeklyHours),
        challenges: mappedChallenges,
        chatMessages: mappedMessages,
        globalLeaderboard: mappedGlobal,
        pinnedMessage: pinMsg,
        isLoading: false
      });
    } catch (err) {
      console.error('Failed to fetch group data', err);
      set({ isLoading: false });
    }
  },

  // ── Chat ──
  sendMessage: async (userId, userName, userAvatar, tag, text) => {
    const groupId = get().groupId;
    if (!groupId) return { success: false };

    try {
      const dbTag = tag.toLowerCase().replace(' ', '_');
      await chatApi.send(groupId, { topicTag: dbTag, content: text });
      
      // Fetch messages again to ensure full client/server sync
      await get().fetchMessages();
      return { success: true };
    } catch (e) {
      console.error(e);
      return { success: false };
    }
  },

  fetchMessages: async () => {
    const groupId = get().groupId;
    if (!groupId) return [];

    try {
      const messagesRes = await chatApi.getMessages(groupId);
      const mappedMessages = messagesRes.map(m => ({
        id: m.id,
        userId: m.user_id,
        userName: m.display_name,
        userAvatar: m.avatar_index,
        tag: m.topic_tag === 'study_help' ? 'Study Help' : m.topic_tag === 'resource' ? 'Resource Share' : m.topic_tag === 'challenge' ? 'Challenge' : 'Off-topic',
        text: m.content,
        timestamp: new Date(m.created_at * 1000).toISOString()
      })).reverse();

      set({ chatMessages: mappedMessages });
      return mappedMessages;
    } catch (err) {
      console.error(err);
      return [];
    }
  },

  setPinnedMessage: async (message) => {
    const groupId = get().groupId;
    if (!groupId) return;
    try {
      await chatApi.pin(groupId, message.text);
      set({ pinnedMessage: message });
    } catch (err) {
      console.error(err);
    }
  },

  // ── Sessions ──
  saveSession: async (sessionData) => {
    try {
      await sessionsApi.save(sessionData);
      // Refresh group data to update study time and ranks
      await get().fetchGroupData(get().groupId);
      return { success: true };
    } catch (e) {
      console.error(e);
      return { success: false };
    }
  },

  // ── Challenges ──
  createChallenge: async (challenge) => {
    try {
      await challengesApi.create(challenge);
      await get().fetchGroupData(get().groupId);
    } catch (e) {
      console.error(e);
    }
  },

  updateChallenge: async (id, updates) => {
    try {
      await challengesApi.update(id, updates);
      await get().fetchGroupData(get().groupId);
    } catch (e) {
      console.error(e);
    }
  },

  deleteChallenge: async (id) => {
    try {
      await challengesApi.delete(id);
      await get().fetchGroupData(get().groupId);
    } catch (e) {
      console.error(e);
    }
  },

  // ── Member Management ──
  assignViceLeader: async (memberId) => {
    const groupId = get().groupId;
    if (!groupId) return;
    try {
      await groupsApi.assignRole(groupId, memberId, 'vice-leader');
      await get().fetchGroupData(groupId);
    } catch (e) {
      console.error(e);
    }
  },

  removeMember: async (memberId) => {
    const groupId = get().groupId;
    if (!groupId) return;
    try {
      await groupsApi.removeMember(groupId, memberId);
      await get().fetchGroupData(groupId);
    } catch (e) {
      console.error(e);
    }
  },

  approveRequest: async (requestId) => {
    try {
      await groupsApi.approveRequest(requestId);
      set(state => ({
        pendingRequests: state.pendingRequests.filter(r => r.id !== requestId)
      }));
      await get().fetchGroupData(get().groupId);
    } catch (e) {
      console.error(e);
    }
  },

  rejectRequest: async (requestId) => {
    try {
      await groupsApi.rejectRequest(requestId, 'Request was declined by leader');
      set(state => ({
        pendingRequests: state.pendingRequests.filter(r => r.id !== requestId)
      }));
    } catch (e) {
      console.error(e);
    }
  },

  togglePaid: (memberId) => {
    set(state => ({
      members: state.members.map(m =>
        m.id === memberId ? { ...m, paid: !m.paid } : m
      ),
    }));
  },

  setWeeklyMinimum: async (hours) => {
    const groupId = get().groupId;
    if (!groupId) return;
    try {
      await groupsApi.updateSettings(groupId, { weeklyMinSeconds: hours * 3600 });
      set({ weeklyMinimumHours: hours });
    } catch (e) {
      console.error(e);
    }
  },

  grantEmergencyLeave: async (memberId) => {
    const groupId = get().groupId;
    if (!groupId) return;
    try {
      await groupsApi.grantEmergencyLeave(memberId);
      await get().fetchGroupData(groupId);
    } catch (e) {
      console.error('grantEmergencyLeave failed', e);
    }
  },
}));
